import type { VoteType } from '@readme/iso';

import React, { useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePageContext } from '@Hub/context/CurrentPageContext';

import { ProjectContext, UserContext } from '@core/context';
import useAmplitude, { AMPLITUDE_EVENT } from '@core/hooks/useAmplitude';
import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Notification, { notify } from '@ui/Notification';
import Sparkle from '@ui/Sparkle';
import Tooltip from '@ui/Tooltip';

import ChatInput from './components/ChatInput';
import ChatMessage from './components/ChatMessage';
import EmptyChatOptions from './components/EmptyChatOptions';
import { useChatInterface } from './hooks/useChatInterface';
import { useOwlbotStorage } from './hooks/useOwlbotStorage';
import classes from './index.module.scss';

// Token counting utility
const estimateTokenCount = (text: string): number => {
  if (!text) return 0;
  const cleanText = text.trim();
  const wordCount = cleanText.split(/\s+/).length;
  const charCount = cleanText.length;
  const charBasedEstimate = Math.ceil(charCount / 3.5);
  const wordBasedEstimate = Math.ceil(wordCount * 1.3);
  return Math.ceil((charBasedEstimate + wordBasedEstimate) / 2);
};

export interface Message {
  content: string;
  id: string;
  isLoading?: boolean;
  question?: string;
  readonly role: 'ai' | 'user';
  timestamp: Date;
  vote?: VoteType | null;
}

interface ChatInterfaceProps {
  onClose?: () => void;
}

/**
 * Owlbot chat interface with streaming support
 * Provides real-time AI responses for documentation questions
 */
export default function ChatInterface({ onClose }: ChatInterfaceProps) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'ChatInterface');
  const { project } = useContext(ProjectContext);
  const user = useContext(UserContext);
  const userIdentifier = user.id;
  const pageContext = usePageContext();

  const childSubdomain = project.parent ? project.subdomain : undefined;

  const { loadMessages, saveMessages, clearMessages } = useOwlbotStorage();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { track } = useAmplitude();

  /**
   * Handle streaming updates - update the last ai message in real-time
   */
  const handleStreamUpdate = useCallback((partialContent: string) => {
    setMessages(prev => {
      // Find the loading ai message (typing indicator)
      const loadingMessageIndex = prev.findIndex(msg => msg.role === 'ai' && msg.isLoading);

      if (loadingMessageIndex !== -1) {
        // Update the streaming message content
        const updatedMessages = [...prev];
        updatedMessages[loadingMessageIndex] = { ...updatedMessages[loadingMessageIndex], content: partialContent };
        return updatedMessages;
      }
      return prev;
    });
  }, []);

  /**
   * Handle complete messages - finalize streaming messages or add new ones
   */
  const handleMessage = useCallback(
    (message: Message) => {
      setMessages(prev => {
        // Look for any existing ai message that's loading (typing indicator)
        const loadingMessageIndex = prev.findIndex(msg => msg.role === 'ai' && msg.isLoading);

        if (loadingMessageIndex !== -1) {
          // Replace the typing indicator with the new message
          const updatedMessages = [...prev];
          // Find the user's question for this AI response
          const userMessage = prev
            .slice(0, loadingMessageIndex)
            .reverse()
            .find(msg => msg.role === 'user');

          updatedMessages[loadingMessageIndex] = {
            ...message,
            isLoading: message.isLoading || false,
            question: userMessage?.content,
          };
          const finalMessage = updatedMessages[loadingMessageIndex];

          if (userMessage) {
            const userTokens = estimateTokenCount(userMessage.content);
            const agentTokens = estimateTokenCount(finalMessage.content);
            const totalTokens = userTokens + agentTokens;

            track(AMPLITUDE_EVENT.ASK_AI_CHAT_MESSAGE_SENT, {
              totalTokensSent: totalTokens,
              userTokens,
              agentTokens,
              user: userIdentifier,
              userMessage: userMessage.content,
              agentResponse: finalMessage.content,
              model: 'gpt-4o',
              pageUrl: window.location.href,
            });
          }

          return updatedMessages;
        }

        // Otherwise add as new message
        return [...prev, message];
      });
    },
    [track, userIdentifier],
  );

  /**
   * Handle errors - add error message to chat and stop loading
   */
  const handleError = useCallback((error: string) => {
    const errorMessage: Message = {
      id: `error-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      role: 'ai',
      content: error,
      timestamp: new Date(),
    };

    setMessages(prev => {
      const filteredMessages = prev.filter(msg => !msg.isLoading);
      return [...filteredMessages, errorMessage];
    });
  }, []);

  /**
   * Initialize chat hook with streaming support
   */
  const { streamingMessageId, sendMessage, sendVote, abortStream } = useChatInterface({
    childSubdomain,
    messages,
    onMessage: handleMessage,
    onStreamUpdate: handleStreamUpdate,
    onError: handleError,
    pageContext,
    projectId: project._id,
  });

  const lastMessageId = messages[messages.length - 1]?.id;
  const lastMessageContent = messages[messages.length - 1]?.content;

  // Load messages from localStorage on mount
  useEffect(() => {
    const storedMessages = loadMessages();
    if (storedMessages.length > 0) {
      setMessages(storedMessages);
    }
  }, [loadMessages]);

  // Auto-scroll to bottom when new messages are added or content updates
  useEffect(() => {
    if (messages.length > 0) {
      const timer = setTimeout(() => {
        if (messagesContainerRef.current) {
          // Scroll to the absolute bottom of the container
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        } else if (messagesEndRef.current) {
          // Fallback to scrollIntoView if container ref is not available
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [messages.length, lastMessageId, lastMessageContent]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(messages);
    }
  }, [messages, saveMessages]);

  /**
   * Handle sending user messages - calls the real API endpoint
   */
  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) {
        return;
      }

      // Add user message immediately with unique ID
      const userMessage: Message = {
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      // Add placeholder ai message with typing indicator immediately
      const aiMessageId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const typingMessage: Message = {
        id: aiMessageId,
        role: 'ai',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      };
      setMessages(prev => [...prev, typingMessage]);

      try {
        await sendMessage(content.trim());
      } catch (error) {
        // Remove the typing message on error
        setMessages(prev => prev.filter(msg => msg.id !== aiMessageId));
        notify(
          <Notification description={t('owlbotChat.sendFailure')} kind="error" title={t('owlbotChat.failureTitle')} />,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, sendMessage, t],
  );

  /**
   * Handle voting on messages
   */
  const handleVote = useCallback(
    async (messageId: string, voteType: VoteType) => {
      const currentMessage = messages.find(m => m.id === messageId);
      if (!currentMessage || currentMessage.role !== 'ai') return;

      const previousVote = currentMessage.vote ?? null;
      const newVote = previousVote === voteType ? null : voteType;
      setMessages(prev => prev.map(msg => (msg.id === messageId ? { ...msg, vote: newVote } : msg)));

      if (newVote) {
        const messageIndex = messages.findIndex(m => m.id === messageId);
        let userQuestion: string | undefined;
        for (let i = messageIndex - 1; i >= 0; i -= 1) {
          if (messages[i].role === 'user') {
            userQuestion = messages[i].content;
            break;
          }
        }

        if (userQuestion) {
          const ok = await sendVote(currentMessage.content, userQuestion, newVote);
          if (!ok) {
            setMessages(prev => prev.map(msg => (msg.id === messageId ? { ...msg, vote: previousVote } : msg)));
            notify(
              <Notification
                description={t('owlbotChat.voteFailure')}
                kind="error"
                title={t('owlbotChat.failureTitle')}
              />,
            );
          }
        }
      }
    },
    [messages, sendVote, t],
  );

  return (
    <div className={classy(bem('&'), 'rm-AskAi-chat')}>
      <div className={bem('-container')}>
        {/* Header - different headers for empty vs normal state */}
        {messages.length === 0 ? (
          <div className={bem('-headerEmpty')}>
            <Tooltip asTitle content={t('owlbotChat.closeChat')}>
              <Button
                aria-label={t('owlbotChat.closeChat')}
                className={bem('-headerButton')}
                ghost
                kind="secondary"
                onClick={() => {
                  onClose?.();
                }}
                size="xs"
              >
                <Icon aria-hidden="true" name="x" />
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div className={bem('-header')}>
            <Sparkle className={bem('-headerIcon')} />
            <span className={bem('-headerTitle')}>{t('owlbotChat.assistant')}</span>
            <Flex align="center" gap="sm">
              <Tooltip asTitle content={t('owlbotChat.clearChat')}>
                <Button
                  aria-label={t('owlbotChat.clearChat')}
                  className={bem('-headerButton')}
                  ghost
                  kind="secondary"
                  onClick={() => {
                    if (isLoading || streamingMessageId) {
                      abortStream();
                    }
                    setMessages([]);
                    clearMessages();
                  }}
                  size="xs"
                  type="button"
                >
                  <Icon aria-hidden="true" name="trash" />
                </Button>
              </Tooltip>
              <Tooltip asTitle content={t('owlbotChat.closeChat')}>
                <Button
                  aria-label={t('owlbotChat.closeChat')}
                  className={bem('-headerButton')}
                  ghost
                  kind="secondary"
                  onClick={() => {
                    onClose?.();
                  }}
                  size="xs"
                >
                  <Icon aria-hidden="true" name="x" />
                </Button>
              </Tooltip>
            </Flex>
          </div>
        )}

        {/* Messages area - conditional rendering based on message count */}
        {messages.length === 0 ? (
          <div className={bem('-emptyChat')}>
            <EmptyChatOptions isLoading={isLoading} onSendMessage={handleSendMessage} />
          </div>
        ) : (
          <div ref={messagesContainerRef} className={bem('-messagesArea')}>
            <div className={bem('-messages')}>
              {messages.map(message => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onVote={handleVote}
                  showTypingIndicator={
                    message.isLoading || (!!streamingMessageId && message.id === streamingMessageId && !message.content)
                  }
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Input area - only show at bottom when there are messages */}
        {messages.length > 0 && (
          <div className={bem('-inputArea')}>
            <ChatInput isLoading={isLoading} onSendMessage={handleSendMessage} />
          </div>
        )}
      </div>
    </div>
  );
}
